import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Calendar, 
  User, 
  Monitor, 
  Globe,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  AlertCircle,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Auditoria = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    operation: 'all',
    entity_type: 'all',
    user_email: '',
    date_from: '',
    date_to: ''
  });

  const entityTypes = [
    { value: 'requerimiento', label: 'Requerimiento' },
    { value: 'inspeccion', label: 'Inspección' },
    { value: 'atencion', label: 'Atención' },
    { value: 'acta_diaria', label: 'Acta Diaria' }
  ];

  const loadOperations = useCallback(async () => {
    try {
      const response = await api.get('/audit/operations');
      setOperations(response.data || []);
    } catch (error) {
      console.error('Error loading operations:', error);
    }
  }, []);

  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.pageSize);
      
      if (filters.operation && filters.operation !== 'all') params.append('operation', filters.operation);
      if (filters.entity_type && filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await api.get(`/audit/logs?${params.toString()}`);
      
      setLogs(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        page: response.data.page,
        total: response.data.total
      }));
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Error al cargar los registros de auditoría');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize]);

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  useEffect(() => {
    loadLogs(1);
  }, [filters]);

  const handleSearch = () => {
    loadLogs(1);
  };

  const handleClearFilters = () => {
    setFilters({
      operation: 'all',
      entity_type: 'all',
      user_email: '',
      date_from: '',
      date_to: ''
    });
  };

  const handleViewDetail = async (log) => {
    try {
      const response = await api.get(`/audit/logs/${log.id}`);
      setSelectedLog(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error al cargar el detalle');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEntityTypeBadge = (type) => {
    const colors = {
      requerimiento: 'bg-blue-100 text-blue-800',
      inspeccion: 'bg-purple-100 text-purple-800',
      atencion: 'bg-green-100 text-green-800',
      acta_diaria: 'bg-orange-100 text-orange-800'
    };
    const labels = {
      requerimiento: 'Requerimiento',
      inspeccion: 'Inspección',
      atencion: 'Atención',
      acta_diaria: 'Acta Diaria'
    };
    return (
      <Badge className={`${colors[type] || 'bg-gray-100 text-gray-800'} font-medium`}>
        {labels[type] || type}
      </Badge>
    );
  };

  const getOperationBadge = (operation) => {
    const isUpdate = operation?.startsWith('UPDATE');
    const isFinish = operation?.startsWith('FINISH');
    
    if (isFinish) {
      return <Badge className="bg-emerald-100 text-emerald-800">Finalización</Badge>;
    }
    if (isUpdate) {
      return <Badge className="bg-amber-100 text-amber-800">Actualización</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800">{operation}</Badge>;
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="auditoria-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#2D2D2D]">
            Auditoría del Sistema
          </h1>
          <p className="text-sm text-[#888888] mt-1">
            Registro de transacciones y cambios importantes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter size={16} />
            <span className="hidden sm:inline">Filtros</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadLogs(pagination.page)}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-[#E8E8E7]">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <Label className="text-xs text-[#5A5A5A] mb-1.5 block">Operación</Label>
                <Select
                  value={filters.operation}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, operation: value }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {operations.map(op => (
                      <SelectItem key={op.code} value={op.code}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-[#5A5A5A] mb-1.5 block">Tipo de Entidad</Label>
                <Select
                  value={filters.entity_type}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, entity_type: value }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {entityTypes.map(et => (
                      <SelectItem key={et.value} value={et.value}>
                        {et.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-[#5A5A5A] mb-1.5 block">Usuario</Label>
                <Input
                  placeholder="Buscar por email..."
                  value={filters.user_email}
                  onChange={(e) => setFilters(prev => ({ ...prev, user_email: e.target.value }))}
                  className="h-9"
                />
              </div>

              <div>
                <Label className="text-xs text-[#5A5A5A] mb-1.5 block">Desde</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                  className="h-9"
                />
              </div>

              <div>
                <Label className="text-xs text-[#5A5A5A] mb-1.5 block">Hasta</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                  className="h-9"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
              >
                Limpiar
              </Button>
              <Button
                size="sm"
                onClick={handleSearch}
                className="bg-[#9d8272] hover:bg-[#8a7264]"
              >
                <Search size={16} className="mr-2" />
                Buscar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-[#E8E8E7]">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Activity size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-[#888888]">Total Registros</p>
                <p className="text-lg font-semibold text-[#2D2D2D]">{pagination.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-[#E8E8E7]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw size={32} className="animate-spin text-[#9d8272] mx-auto mb-3" />
                <p className="text-sm text-[#888888]">Cargando registros...</p>
              </div>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle size={48} className="text-[#E8E8E7] mb-3" />
              <p className="text-[#888888]">No se encontraron registros de auditoría</p>
              <p className="text-sm text-[#AAAAAA] mt-1">
                Los registros aparecerán cuando se realicen transacciones auditables
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5F5]">
                      <TableHead className="font-semibold text-[#2D2D2D]">Fecha/Hora</TableHead>
                      <TableHead className="font-semibold text-[#2D2D2D]">Usuario</TableHead>
                      <TableHead className="font-semibold text-[#2D2D2D]">Operación</TableHead>
                      <TableHead className="font-semibold text-[#2D2D2D]">Entidad</TableHead>
                      <TableHead className="font-semibold text-[#2D2D2D]">Código</TableHead>
                      <TableHead className="font-semibold text-[#2D2D2D]">Resumen</TableHead>
                      <TableHead className="font-semibold text-[#2D2D2D] text-center">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-[#F9F9F9]">
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Clock size={14} className="text-[#888888]" />
                            <span className="text-sm">{formatDate(log.timestamp)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium text-[#2D2D2D]">{log.user_nombre || '-'}</p>
                            <p className="text-xs text-[#888888]">{log.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {getOperationBadge(log.operation)}
                            <p className="text-xs text-[#888888]">{log.operation_label}</p>
                          </div>
                        </TableCell>
                        <TableCell>{getEntityTypeBadge(log.entity_type)}</TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-[#9d8272]">
                            {log.entity_code || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <p className="text-sm text-[#5A5A5A] truncate" title={log.changes_summary}>
                            {log.changes_summary || '-'}
                          </p>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetail(log)}
                            className="text-[#9d8272] hover:text-[#8a7264]"
                          >
                            <Eye size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden divide-y divide-[#E8E8E7]">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="p-4 hover:bg-[#F9F9F9] cursor-pointer"
                    onClick={() => handleViewDetail(log)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {getEntityTypeBadge(log.entity_type)}
                          {getOperationBadge(log.operation)}
                        </div>
                        <p className="text-sm font-medium text-[#2D2D2D]">
                          {log.operation_label}
                        </p>
                        <p className="text-xs text-[#888888] mt-1">
                          {log.user_nombre} • {log.entity_code}
                        </p>
                        <p className="text-xs text-[#AAAAAA] mt-1 flex items-center gap-1">
                          <Clock size={12} />
                          {formatDate(log.timestamp)}
                        </p>
                      </div>
                      <Eye size={18} className="text-[#888888] flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {logs.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t border-[#E8E8E7]">
              <p className="text-sm text-[#888888]">
                Mostrando {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} de {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadLogs(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loading}
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm text-[#5A5A5A]">
                  {pagination.page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadLogs(pagination.page + 1)}
                  disabled={pagination.page >= totalPages || loading}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-[#9d8272]" />
              Detalle de Auditoría
            </DialogTitle>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-6">
              {/* General Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#888888]">Fecha y Hora</Label>
                  <p className="text-sm font-medium">{formatDate(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Operación</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {getOperationBadge(selectedLog.operation)}
                    <span className="text-sm">{selectedLog.operation_label}</span>
                  </div>
                </div>
              </div>

              {/* User Info */}
              <Card className="border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User size={16} className="text-[#9d8272]" />
                    Usuario
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#888888]">Nombre:</span>
                    <p className="font-medium">{selectedLog.user_nombre || '-'}</p>
                  </div>
                  <div>
                    <span className="text-[#888888]">Email:</span>
                    <p className="font-medium">{selectedLog.user_email}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Entity Info */}
              <Card className="border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText size={16} className="text-[#9d8272]" />
                    Entidad Afectada
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#888888]">Tipo:</span>
                    <div className="mt-1">{getEntityTypeBadge(selectedLog.entity_type)}</div>
                  </div>
                  <div>
                    <span className="text-[#888888]">Código:</span>
                    <p className="font-mono font-medium text-[#9d8272]">{selectedLog.entity_code || '-'}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Changes Summary */}
              {selectedLog.changes_summary && (
                <Card className="border-[#E8E8E7]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Resumen de Cambios</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#5A5A5A] whitespace-pre-wrap">
                      {selectedLog.changes_summary}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Old/New Values */}
              {(selectedLog.old_values || selectedLog.new_values) && (
                <Card className="border-[#E8E8E7]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Valores Modificados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedLog.old_values && (
                        <div>
                          <Label className="text-xs text-red-600 mb-2 block">Valores Anteriores</Label>
                          <pre className="text-xs bg-red-50 p-3 rounded-md overflow-auto max-h-40">
                            {JSON.stringify(selectedLog.old_values, null, 2)}
                          </pre>
                        </div>
                      )}
                      {selectedLog.new_values && (
                        <div>
                          <Label className="text-xs text-green-600 mb-2 block">Valores Nuevos</Label>
                          <pre className="text-xs bg-green-50 p-3 rounded-md overflow-auto max-h-40">
                            {JSON.stringify(selectedLog.new_values, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Device Info */}
              <Card className="border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Monitor size={16} className="text-[#9d8272]" />
                    Información del Dispositivo
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-[#888888]">Dispositivo:</span>
                    <p className="font-medium capitalize">{selectedLog.device_type || '-'}</p>
                  </div>
                  <div>
                    <span className="text-[#888888]">Navegador:</span>
                    <p className="font-medium">{selectedLog.browser || '-'}</p>
                  </div>
                  <div>
                    <span className="text-[#888888]">Sistema:</span>
                    <p className="font-medium">{selectedLog.os || '-'}</p>
                  </div>
                  <div>
                    <span className="text-[#888888]">IP:</span>
                    <p className="font-mono text-xs">{selectedLog.ip_address || '-'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auditoria;
